import { Controller } from "@hotwired/stimulus"
import { toBlob } from "html-to-image"

// A figure's Share card (release_watch/watches/_share): opens and closes its
// dialog (Esc, or a click outside the card, closes it too). The card has one
// wide size everywhere, scaled down to fit the dialog. Opening it draws
// the card as a PNG (twice its size, so it's sharp) and shows that in its
// place, so a right-click copies it. Copy links copies the prices, as
// Discord's markdown with an HTML copy beside it for apps that keep links
// (where the clipboard API is refused, the old select-and-copy does it);
// Copy card copies the picture. A button that's copied turns green with a
// tick ("Card copied") and stays so until the dialog closes, so you can see
// which you've pasted; one that couldn't says so for a moment: Copy card says
// "Couldn't draw the card" when the card never became a picture (a picture on
// it the browser wouldn't draw, logged in the console), and only says to
// right-click it when the clipboard was refused. Picture opens
// a row of the figure's pictures: picking one puts it on the card and draws
// the card again (anything copied before is the old card, so the buttons
// start over).
// The CSS properties `root` and what's in it (and their ::before/::after)
// set to anything but the browser's own default for that kind of element:
// the only ones its picture needs. Copying every property onto every
// element, and the site's CSS variables with them, made a card of many
// stores (Jungle Huntress: 117 elements, ~19 KB of styles each) a 2.5 MB
// picture, more than Chrome will load as one.
// The defaults come from a blank page that's laid out (off screen): Firefox
// gives a hidden iframe's elements the wrong ones ("border-style: solid"),
// which dropped the card's borders from its picture.
async function usedStyleProperties(root) {
  const blank = Object.assign(document.createElement("iframe"), { srcdoc: "<!doctype html><title></title>" })
  blank.style.cssText = "position: fixed; left: -10000px; top: 0; width: 100px; height: 100px; border: 0; visibility: hidden"
  const loaded = new Promise((resolve) => blank.addEventListener("load", resolve, { once: true }))
  document.body.append(blank)
  await loaded
  const defaults = {}
  const names = Array.from(getComputedStyle(document.documentElement)).filter((name) => !name.startsWith("--"))
  const used = new Set()
  const compare = (style, plain) => names.forEach((name) => {
    if (!used.has(name) && style.getPropertyValue(name) !== plain.getPropertyValue(name)) used.add(name)
  })
  for (const element of [ root, ...root.querySelectorAll("*") ]) {
    const plain = defaults[element.tagName] ??= getComputedStyle(blank.contentDocument.body.appendChild(blank.contentDocument.createElement(element.tagName)))
    compare(getComputedStyle(element), plain)
    for (const pseudo of [ "::before", "::after" ]) {
      const style = getComputedStyle(element, pseudo)
      if (style.content !== "none" && style.content !== "normal") compare(style, plain)
    }
  }
  blank.remove()
  return [ ...used ]
}

// Each picture on the card swapped for the pixels this page already shows
// (as a WebP data URL, at most twice its size on the card), so html-to-image
// doesn't download them again. A download of its own could come back empty
// (a store logo's did in one Firefox, and the card wouldn't draw), and
// a big photo made the card's picture several MB. One that never loaded
// stops the card with its address, rather than drawing a blank.
async function inlinePictures(root) {
  for (const img of root.querySelectorAll("img")) {
    if (img.src.startsWith("data:")) continue
    if (!img.complete) await new Promise((resolve) => ["load", "error"].forEach((type) => img.addEventListener(type, resolve, { once: true })))
    if (!img.naturalWidth) throw new Error(`A picture on the card didn't load: ${img.src}`)
    const scale = Math.min(1, (2 * Math.max(img.offsetWidth, img.offsetHeight, 1)) / Math.max(img.naturalWidth, img.naturalHeight))
    const canvas = Object.assign(document.createElement("canvas"), { width: Math.round(img.naturalWidth * scale) || 1, height: Math.round(img.naturalHeight * scale) || 1 })
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height)
    img.src = canvas.toDataURL("image/webp", 0.9)
  }
}

// The card's picture column and its frame (watch.css), as wide as a
// landscape picture may make the column.
const PICTURE_WIDTH = 320
const FRAME_WIDTH = 1216
const WIDEST_PICTURE = 760

export default class extends Controller {
  static targets = [ "dialog", "text", "html", "stage", "frame", "shot", "image", "button", "pic", "pictures", "pictureButton", "drawing" ]

  open() {
    this.buttonTargets.forEach((button) => this.reset(button))
    this.dialogTarget.showModal()
    this.fit()
    this.resizing ??= new ResizeObserver(() => this.fit())
    this.resizing.observe(this.stageTarget)
    this.resizing.observe(this.frameTarget)
    this.picture ??= this.drawn(this.sizePicture())
  }

  disconnect() {
    this.resizing?.disconnect()
  }

  // The card shrunk to the stage's width; the stage as tall as it is then.
  // (Its picture, once shown, scales itself.)
  fit() {
    if (this.frameTarget.hidden) {
      this.stageTarget.style.height = ""
      return
    }
    const scale = Math.min(1, this.stageTarget.clientWidth / this.frameTarget.offsetWidth)
    this.frameTarget.style.transform = scale < 1 ? `scale(${scale})` : ""
    this.stageTarget.style.height = `${this.frameTarget.offsetHeight * scale}px`
  }

  close() { this.dialogTarget.close() }

  backdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  togglePictures() {
    const open = this.picturesTarget.hidden
    this.picturesTarget.hidden = !open
    this.pictureButtonTarget.setAttribute("aria-expanded", open)
    if (open) this.picturesTarget.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  // Another picture on the card (and its blurred copy round it): the card
  // comes back in place of its old picture until the new one's drawn.
  pick(event) {
    const choice = event.currentTarget
    if (choice.classList.contains("is-on")) return
    this.picturesTarget.querySelectorAll(".rw-share-pick").forEach((button) => {
      button.classList.toggle("is-on", button === choice)
      button.setAttribute("aria-pressed", button === choice)
    })
    this.drawing = (this.drawing || 0) + 1 // a card still drawing has the old picture
    const src = choice.dataset.src
    const fill = Object.assign(document.createElement("img"), { className: "rw-sc-pic-fill", src, alt: "" })
    fill.setAttribute("aria-hidden", "true")
    const picture = Object.assign(document.createElement("img"), { className: "w-full h-full object-contain", src, alt: "" })
    this.picTarget.replaceChildren(fill, picture)
    this.buttonTargets.forEach((button) => this.reset(button))
    this.shotTarget.hidden = true
    this.frameTarget.hidden = false
    this.fit()
    // Copy card, clicked meanwhile, waits for this one.
    this.picture = this.drawn(Promise.all([ fill, picture ].map((img) => img.decode().catch(() => {}))).then(() => this.sizePicture()))
  }

  // A landscape picture gets a wider column, as wide as it takes to fill
  // the card's height (up to WIDEST_PICTURE), and the card grows by as much,
  // so the prices keep their room; a portrait or square one keeps the usual
  // column. (Set on the dialog, which takes the wider card too.)
  async sizePicture() {
    const img = this.picTarget.querySelector("img:not(.rw-sc-pic-fill)")
    if (img && !img.complete) await new Promise((resolve) => [ "load", "error" ].forEach((type) => img.addEventListener(type, resolve, { once: true })))
    const landscape = img?.naturalWidth > img?.naturalHeight
    const width = landscape ? Math.round(Math.min(WIDEST_PICTURE, this.picTarget.offsetHeight * img.naturalWidth / img.naturalHeight)) : PICTURE_WIDTH
    const extra = Math.max(0, width - PICTURE_WIDTH)
    this.dialogTarget.style.setProperty("--rw-sc-pic", `${PICTURE_WIDTH + extra}px`)
    this.dialogTarget.style.setProperty("--rw-share-w", `${FRAME_WIDTH + extra}px`)
    this.fit()
  }

  // draw(), after `ready`; one that fails is logged and forgotten, so the
  // next open or Copy card draws it again.
  // Meanwhile the card is covered ("Drawing the card…") and its buttons
  // wait, until the latest drawing is done (or failed: the card's shown,
  // and Copy card says so).
  drawn(ready = Promise.resolve()) {
    this.busy(true)
    const picture = ready.then(() => this.draw())
    picture.then(() => {
      if (this.picture === picture) this.busy(false)
    }, (error) => {
      console.error("The Share card couldn't be drawn as a picture", error)
      this.report(error)
      if (this.picture !== picture) return
      this.picture = null
      this.busy(false)
    })
    return picture
  }

  busy(drawing) {
    this.drawingTarget.hidden = !drawing
    this.dialogTarget.setAttribute("aria-busy", drawing)
    const buttons = [ ...this.buttonTargets, ...(this.hasPictureButtonTarget ? [ this.pictureButtonTarget ] : []) ]
    buttons.forEach((button) => { button.disabled = drawing })
  }

  // The card as a PNG, swapped in for it once drawn. Resolves to the PNG.
  // (A card drawn after another picture's been picked is dropped.)
  async draw() {
    // At its full size, whatever it's scaled to on this screen.
    const frame = this.frameTarget
    const drawing = this.drawing = (this.drawing || 0) + 1
    // includeQueryParams: its picture cache otherwise drops the query, and every
    // store picture is /picture?url=..., so the first one drawn would stand in for all.
    // includeStyleProperties: only those the card uses (usedStyleProperties).
    await inlinePictures(frame)
    const blob = await toBlob(frame, { pixelRatio: 2, width: frame.offsetWidth, height: frame.offsetHeight, style: { transform: "none" }, includeQueryParams: true, includeStyleProperties: await usedStyleProperties(frame) })
    if (drawing !== this.drawing) return blob
    URL.revokeObjectURL(this.imageTarget.src)
    this.imageTarget.src = URL.createObjectURL(blob)
    await this.imageTarget.decode()
    if (drawing !== this.drawing) return blob
    this.frameTarget.hidden = true
    this.shotTarget.hidden = false
    this.fit()
    return blob
  }

  // A card that wouldn't draw, to the server's log ("[Share card]"), so it
  // can't fail quietly in someone's browser.
  report(error) {
    const detail = error instanceof Error ? `${error.name}: ${error.message}` : `${error?.type || error} ${String(error?.target?.src || "").slice(0, 80)}`
    const body = JSON.stringify({ page: location.pathname, error: detail, agent: navigator.userAgent })
    fetch("/browser_reports/share_card", { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {})
  }

  async copy(event) {
    const button = event.currentTarget
    let copied
    if (button.dataset.copy === "card") {
      // The picture may still be drawing: the clipboard takes its promise, so
      // the click still counts when it's ready.
      const picture = this.picture ??= this.drawn()
      const drawn = picture.then(() => true, () => false)
      try {
        await navigator.clipboard.write([ new ClipboardItem({ "image/png": picture }) ])
        copied = true
      } catch {
        copied = false
      }
      if (copied) this.done(button)
      else this.flash(button, await drawn ? "Right-click it to copy" : "Couldn't draw the card")
    } else {
      copied = await this.copyLinks()
      copied ? this.done(button) : this.flash(button, "Couldn't copy")
    }
  }

  async copyLinks() {
    const text = this.textTarget.value
    try {
      if (window.ClipboardItem) {
        await navigator.clipboard.write([ new ClipboardItem({
          "text/plain": new Blob([ text ], { type: "text/plain" }),
          "text/html": new Blob([ this.htmlTarget.innerHTML ], { type: "text/html" })
        }) ])
      } else {
        await navigator.clipboard.writeText(text)
      }
      return true
    } catch {
      return this.copyOldWay(text)
    }
  }

  copyOldWay(text) {
    const area = document.createElement("textarea")
    area.value = text
    area.setAttribute("readonly", "")
    area.style.cssText = "position: fixed; top: 0; left: -9999px; opacity: 0"
    this.dialogTarget.append(area)
    area.select()
    let copied = false
    try { copied = document.execCommand("copy") } catch { copied = false }
    area.remove()
    return copied
  }

  // Copied: green, a tick, "Card copied", until the dialog closes.
  done(button) {
    clearTimeout(button.flashTimer)
    this.label(button).textContent = button.dataset.copied
    button.querySelector("i").dataset.icon ??= button.querySelector("i").className
    button.querySelector("i").className = "fas fa-check"
    button.classList.remove("is-copied")
    void button.offsetWidth // restarts the pop when it's copied again
    button.classList.add("is-copied")
  }

  reset(button) {
    button.classList.remove("is-copied")
    const label = this.label(button)
    if (label.dataset.was) label.textContent = label.dataset.was
    const icon = button.querySelector("i")
    if (icon.dataset.icon) icon.className = icon.dataset.icon
  }

  label(button) {
    const label = button.querySelector("span")
    label.dataset.was ??= label.textContent
    return label
  }

  flash(button, message) {
    this.reset(button)
    this.label(button).textContent = message
    clearTimeout(button.flashTimer)
    button.flashTimer = setTimeout(() => this.reset(button), 2500)
  }
}
